const actions = {
  /**
   * Updates a message view, hiding it via the isActive property.
   * @param {VuexContext} context VuexContext
   * @param {MessageRequest} MessageRequest
   * @returns {MessageView} A MessageView with an isActive property set to false.
   */
  hideMessage({ commit }, { componentName, message }) {
    const view = {
      componentName,
      message: { ...message, isActive: false },
    }

    commit('view', view)

    return view
  },
  /**
   * Updates a message view, revealing it via the isActive property.
   * @param {VuexContext} context VuexContext
   * @param {MessageRequest} MessageRequest
   * @returns {MessageView} A MessageView with an isActive property set to true.
   */
  showMessage({ commit }, { componentName, message }) {
    const view = {
      componentName,
      message: { ...message, isActive: true },
    }

    commit('view', view)

    return view
  },
  /**
   * Updates and hides an error message view, using the error's message as the view's text.
   * @param {VuexContext} context VuexContext
   * @param {ErrorMessageRequest} ErrorMessageRequest
   * @returns {Promise<MessageView>} MessageView
   */
  hideErrorMessage({ dispatch }, { componentName, error }) {
    const type = 'error'
    const { message: text } = error
    const message = { ...error, text, type }

    return dispatch('hideMessage', { componentName, message })
  },
  /**
   * Updates and reveals an error message view, using the error's message as the view's text.
   * @param {VuexContext} context VuexContext
   * @param {ErrorMessageRequest} ErrorMessageRequest
   * @returns {Promise<MessageView>} MessageView
   */
  showErrorMessage({ dispatch }, { componentName, error }) {
    const type = 'error'
    const { message: text } = error
    const message = { ...error, text, type }

    return dispatch('showMessage', { componentName, message })
  },
  /**
   * Updates and hides a loading message view.
   * @param {VuexContext} context VuexContext
   * @param {LoadingMessageRequest} LoadingMessageRequest
   * @returns {Promise<MessageView>} MessageView
   */
  hideLoadingMessage({ dispatch }, { componentName, text }) {
    const type = 'spinner'
    const message = { text, type }

    return dispatch('hideMessage', { componentName, message })
  },
  /**
   * Updates and reveals a loading message view with the given text beneath it.
   * @param {VuexContext} context VuexContext
   * @param {LoadingMessageRequest} LoadingMessageRequest
   * @returns {Promise<MessageView>} MessageView
   */
  showLoadingMessage({ dispatch }, { componentName, text }) {
    const type = 'spinner'
    const message = { text, type }

    return dispatch('showMessage', { componentName, message })
  },
  /**
   * Updates and hides a status message view, using the status code to look up the message text.
   * @param {VuexContext} context VuexContext
   * @param {StatusMessageRequest} StatusMessageRequest
   * @return {Promise<MessageView>} MessageView
   */
  hideStatusMessage({ dispatch, getters }, { componentName, statusCode }) {
    const { statusCodes } = getters
    const message = statusCodes[statusCode]

    return dispatch('hideMessage', { componentName, message })
  },
  /**
   * Updates and reveals a status message view, using the status code to look up the message text.
   * @param {VuexContext} context VuexContext
   * @param {StatusMessageRequest} StatusMessageRequest
   * @return {Promise<MessageView>} MessageView
   */
  showStatusMessage({ dispatch, getters }, { componentName, statusCode }) {
    const { statusCodes } = getters
    const message = statusCodes.find((status) => status.code === statusCode)

    return dispatch('showMessage', { componentName, message })
  },
}

export default actions
